import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Phone, ArrowRight, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SafeHtml from '@/components/SafeHtml';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ZoningCheckForm from '@/components/ZoningCheckForm';
import ExitIntentPopup from '@/components/ExitIntentPopup';
import SocialProofBanner from '@/components/SocialProofBanner';
import LeadMagnetCTA from '@/components/LeadMagnetCTA';
import DevelopmentEnquiryTool from '@/components/DevelopmentEnquiryTool';
import SEO, { articleSchema as articleSchemaHelper, breadcrumbSchema } from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { autoLinkContent, findRelatedBySuburbs } from '@/utils/autoLinker';
import { allInsightsArticles } from '@/data/allInsightsArticles';
import { sk } from '@/utils/stableKey';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const articles = allInsightsArticles;

/* ── Custom Markdown Components ── */
const mdComponents = {
  h2: ({ children }) => (
    <h2 className="font-heading text-3xl font-light text-[#042E0C] mt-16 mb-6 pb-4 border-b border-[#C4A36B]/30 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-heading text-xl font-medium text-[#042E0C] mt-10 mb-4">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="text-[#3a3a3a] text-base leading-[1.85] mb-6">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="space-y-3 mb-8 ml-1">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="space-y-3 mb-8 ml-1 list-decimal list-inside">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="flex items-start gap-3 text-[#3a3a3a] text-base leading-relaxed">
      <span className="w-1.5 h-1.5 bg-[#C4A36B] rounded-full mt-2.5 flex-shrink-0" />
      <span>{children}</span>
    </li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-[#042E0C]">{children}</strong>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#C4A36B] bg-[#FFF4EA] px-6 py-5 my-8 italic text-[#042E0C]/80">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-8 border border-[#042E0C]/10">
      <table className="w-full text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[#042E0C] text-[#FFF4EA]">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-5 py-3 text-left font-medium text-sm tracking-wide">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-5 py-3.5 border-b border-[#042E0C]/10 text-[#3a3a3a]">{children}</td>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-[#FFF4EA]/50 transition-colors">{children}</tr>
  ),
  hr: () => (
    <div className="my-12 flex items-center gap-4">
      <div className="flex-1 h-px bg-[#C4A36B]/30" />
      <div className="w-2 h-2 bg-[#C4A36B] rotate-45" />
      <div className="flex-1 h-px bg-[#C4A36B]/30" />
    </div>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-[#C4A36B] font-medium underline underline-offset-2 decoration-[#C4A36B]/40 hover:decoration-[#C4A36B] transition-colors">
      {children}
    </a>
  ),
};

/* ── Inline CTA Box ── */
const InlineCTA = ({ title, description, linkTo, linkText }) => (
  <div className="my-10 bg-[#042E0C] p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
    <div>
      <p className="font-heading text-lg text-[#FFF4EA] mb-1">{title}</p>
      <p className="text-sm text-[#FFF4EA]/60">{description}</p>
    </div>
    <Link
      to={linkTo}
      className="inline-flex items-center gap-2 px-6 py-3 bg-[#C4A36B] text-white text-sm font-medium tracking-wide hover:bg-[#FFF4EA] hover:text-[#042E0C] transition-colors whitespace-nowrap flex-shrink-0"
    >
      {linkText} <ArrowRight size={16} />
    </Link>
  </div>
);

/* ── Related Articles ── */
const RelatedArticles = ({ currentId, currentCategory, suburbRelated = [] }) => {
  // Build a keyword set from the current article's id
  const idKeywords = currentId.split('-').filter(w => w.length > 3);
  
  // Score articles by relevance: suburb > category > keyword overlap > general
  const scored = articles
    .filter(a => a.id !== currentId)
    .map(a => {
      let score = 0;
      // Suburb-related articles get highest boost
      if (suburbRelated.find(r => r.id === a.id)) score += 10;
      // Same category
      if (a.category === currentCategory) score += 5;
      // Keyword overlap in slug
      const aKeywords = a.id.split('-').filter(w => w.length > 3);
      const overlap = idKeywords.filter(kw => aKeywords.includes(kw)).length;
      score += overlap * 2;
      return { ...a, _score: score };
    })
    .sort((a, b) => b._score - a._score)
    .slice(0, 4);
  
  if (scored.length === 0) return null;
  
  return (
    <section className="bg-[#FFF4EA] py-16" data-testid="related-articles">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <p className="text-xs font-semibold tracking-widest uppercase text-[#C4A36B] mb-3 text-center">Keep Exploring</p>
        <h2 className="font-heading text-2xl font-light text-[#042E0C] mb-10 text-center">You Might Also Be Interested In</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {scored.map(a => (
            <Link key={a.id} to={`/insights/${a.id}`} className="group block bg-white border border-[#042E0C]/10 hover:border-[#C4A36B] transition-all hover:shadow-lg overflow-hidden">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="p-4">
                <span className="text-xs text-[#C4A36B] font-semibold tracking-wider uppercase">{a.category}</span>
                <h3 className="font-heading text-sm text-[#042E0C] mt-2 group-hover:text-[#C4A36B] transition-colors leading-snug line-clamp-2">
                  {a.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── FAQ Section ── */
const FAQSection = ({ faq }) => {
  if (!faq || faq.length === 0) return null;
  return (
    <div className="my-12 bg-[#FFF4EA] border border-[#C4A36B]/20 p-8 sm:p-10">
      <h2 className="font-heading text-2xl font-light text-[#042E0C] mb-8">Frequently Asked Questions</h2>
      <div className="space-y-6">
        {faq.map((item, idx) => (
          <div key={sk(item, idx)} className="border-b border-[#042E0C]/10 pb-6 last:border-0 last:pb-0">
            <h3 className="font-heading text-lg text-[#042E0C] mb-2">{item.q}</h3>
            <p className="text-[#3a3a3a] text-base leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Table of Contents ── */
const TableOfContents = ({ content }) => {
  const headings = content.match(/^## .+$/gm);
  if (!headings || headings.length < 3) return null;
  return (
    <nav className="mb-12 bg-[#FFF4EA] border border-[#042E0C]/10 p-6 sm:p-8">
      <h3 className="font-heading text-sm font-semibold text-[#042E0C] uppercase tracking-wider mb-4">In This Article</h3>
      <ul className="space-y-2.5">
        {headings.map((h, i) => {
          const text = h.replace('## ', '');
          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          return (
            <li key={sk(h, i)}>
              <a href={`#${id}`} className="flex items-center gap-2 text-sm text-[#042E0C]/70 hover:text-[#C4A36B] transition-colors">
                <ChevronRight size={14} className="text-[#C4A36B]" />
                {text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/* ── Main Article Page ── */
const SELLER_CATEGORIES = new Set(['Seller Guide', 'Landowner Guide']);
const SELLER_ID_KEYWORDS = ['sell', 'landowner', 'zoning', 'can-i-build', 'overlay', 'planning', 'subdivision', 'subdivide', 'downsizing', 'duplex', 'townhouse', 'granny-flat', 'knock-down', 'rezone', 'what-is-my', 'property-worth', 'infrastructure-charges', 'town-planner', 'unapproved', 'da-refusal', 'heritage', 'character', 'flood', 'bushfire', 'vegetation', 'airport', 'acid-sulfate', 'corridor', 'block-worth', 'corner-block', 'splitter-block', 'second-house', 'extra-storey', 'costly-mistakes', 'neighbour-renovation', 'council-rejected', 'what-developers-look', 'how-to-maximise', 'development-site-sales', 'understanding-zoning'];
const BUYER_ID_KEYWORDS = ['how-to-buy', 'buying', 'what-is-a-development-site', 'how-property-advisory', 'inner-city-brisbane-suburbs-to-watch', 'brisbane-property-market-outlook', 'best-suburbs-first-home'];

const isSellerArticle = (article) => {
  if (!article) return false;
  const id = article.id || '';
  // Exclude buyer articles first
  if (BUYER_ID_KEYWORDS.some(kw => id.includes(kw))) return false;
  if (SELLER_CATEGORIES.has(article.category)) return true;
  return SELLER_ID_KEYWORDS.some(kw => id.includes(kw));
};

const getLeadMagnetSlug = (article) => {
  const id = article?.id || '';
  if (id.includes('overlay') || id.includes('flood') || id.includes('heritage') || id.includes('character') || id.includes('bushfire') || id.includes('vegetation') || id.includes('airport') || id.includes('acid-sulfate') || id.includes('corridor')) return 'property-overlay-guide-seq';
  if (id.includes('sell') || id.includes('developer') || id.includes('landowner') || id.includes('downsizing')) return 'selling-to-developer-guide';
  return 'brisbane-zoning-cheat-sheet';
};

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cmsArticle, setCmsArticle] = useState(null);
  const [cmsLoading, setCmsLoading] = useState(false);

  const staticArticle = articles.find(a => a.id === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCmsArticle(null);
    setCmsLoading(true);
    // Always check the Content Hub first — the CMS copy is the source of
    // truth (an imported/edited article overrides the code version).
    fetch(`${API_URL}/api/articles/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.article) setCmsArticle(data.article); })
      .catch(() => {})
      .finally(() => setCmsLoading(false));
    fetch(`${API_URL}/api/articles/${slug}/view`, { method: 'POST' }).catch(() => {});
  }, [slug]);

  const article = (cmsArticle ? {
    id: cmsArticle.slug,
    title: cmsArticle.title,
    excerpt: cmsArticle.excerpt,
    content: cmsArticle.content,
    category: cmsArticle.category,
    image: cmsArticle.image,
    metaDescription: cmsArticle.meta_description,
    date: cmsArticle.published_at || cmsArticle.created_at,
    readTime: `${Math.max(1, Math.ceil((cmsArticle.content || '').replace(/<[^>]*>/g, '').split(/\s+/).length / 200))} min read`,
    faq: cmsArticle.faq,
    _isCms: true,
  } : staticArticle);

  // Only block with a spinner when there's no code fallback to show yet.
  if (cmsLoading && !staticArticle && !cmsArticle) {
    return (
      <div className="min-h-screen bg-[#FFF4EA]">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-2 border-[#C4A36B] border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FFF4EA]">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-heading text-[#042E0C] mb-4">Article not found</h2>
            <Link to="/insights" className="text-[#C4A36B] hover:underline">← Back to Insights</Link>
          </div>
        </div>
      </div>
    );
  }

  const faqSchema = article.faq ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  const artSchema = articleSchemaHelper({
    title: article.title,
    description: article.metaDescription,
    image: article.image,
    datePublished: article.date,
    dateModified: article.dateModified || article.date,
    url: `/insights/${article.id}`,
    author: article.author || {
      // Default author when none is set on the article — Daniel as Managing
      // Director and ACRES' published expert across the Brisbane
      // development & advisory niche. Adds a real Person to the schema
      // (rather than just Organization) which is what AI retrievers prefer.
      name: 'Daniel McCormack',
      role: 'Managing Director, ACRES',
      url: '/team/daniel-mccormack',
      image: 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/ibqouwi8_F9992471-15B8-4303-8FF6-01B35D3598D1.png',
      sameAs: ['https://linkedin.com/in/danielmccormack', 'https://instagram.com/acres.au'],
    },
  });

  const combinedSchema = [artSchema, breadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Insights', url: '/insights' }, { name: article.category || 'Article', url: `/insights?category=${encodeURIComponent(article.category || '')}` }, { name: article.title }])];
  if (faqSchema) combinedSchema.push(faqSchema);

  // Split content at the FAQ/HR section — we render the FAQ as a styled component instead
  const isCms = article._isCms;
  const mainContent = isCms ? article.content : article.content.split(/\n## Frequently Asked Questions/)[0];

  // Auto-link suburb names and service terms in the content (only for markdown)
  const { content: linkedContent, mentionedSuburbs } = isCms ? { content: '', mentionedSuburbs: [] } : autoLinkContent(mainContent, article.id);

  // Find related articles based on shared suburb mentions
  const suburbRelated = findRelatedBySuburbs(article.id, mentionedSuburbs, articles, 3);

  // Determine inline CTA based on article topic
  const ctaMap = {
    'how-to-buy-a-development-site-in-brisbane': { title: 'Looking for development sites?', description: 'Browse our current stock of development sites across Brisbane.', linkTo: '/stock', linkText: 'View Stock' },
    'brisbane-property-market-outlook-2025-2026': { title: 'Want to discuss Brisbane opportunities?', description: 'Our advisory team provides tailored market intelligence.', linkTo: '/contact', linkText: 'Contact Us' },
    'what-is-a-development-site': { title: 'Ready to explore development sites?', description: 'See what development opportunities are currently available.', linkTo: '/stock', linkText: 'Browse Sites' },
    'how-property-advisory-works-in-australia': { title: 'Explore our advisory services', description: 'See how ACRES can help with your next property decision.', linkTo: '/services', linkText: 'Our Services' },
    'inner-city-brisbane-suburbs-to-watch-for-development': { title: 'See properties in these suburbs', description: 'Browse residential listings across South East Queensland.', linkTo: '/residential', linkText: 'View Listings' },
    'guide-to-selling-property-off-market-in-australia': { title: 'Thinking about selling off-market?', description: 'Talk to our team about a confidential, tailored approach.', linkTo: '/contact', linkText: 'Get in Touch' },
    'how-much-is-my-land-worth-for-development': { title: 'Get a free site appraisal', description: 'ACRES provides complimentary development site assessments.', linkTo: '/contact', linkText: 'Request Appraisal' },
    'should-i-sell-my-property-as-a-development-site': { title: 'Not sure if your property qualifies?', description: 'We assess your zoning, yield, and market demand at no cost.', linkTo: '/contact', linkText: 'Free Assessment' },
    'what-developers-look-for-when-buying-your-land': { title: 'Position your site for maximum value', description: 'Our team knows exactly what developers want.', linkTo: '/contact', linkText: 'Talk to Us' },
    'how-to-maximise-sale-price-of-development-site': { title: 'Ready to sell your development site?', description: 'ACRES achieves premium prices through competitive tension.', linkTo: '/contact', linkText: 'Get Started' },
    'understanding-zoning-property-value': { title: 'Check your property\'s zoning potential', description: 'Free development site assessment across Brisbane & SEQ.', linkTo: '/contact', linkText: 'Request Assessment' },
    'development-site-sales-process-what-to-expect': { title: 'Thinking of selling to a developer?', description: 'We guide you through every stage of the process.', linkTo: '/contact', linkText: 'Contact Us' },
    'conditional-contracts-explained-sellers-guide': { title: 'Need contract advice?', description: 'Our team negotiates contracts that protect your interests.', linkTo: '/contact', linkText: 'Speak with Us' },
    'tax-implications-selling-development-site-australia': { title: 'Get professional property advice', description: 'We connect you with the right professionals for your sale.', linkTo: '/contact', linkText: 'Contact ACRES' },
    'what-is-a-put-and-call-option-sellers-guide': { title: 'Structuring your development site deal', description: 'ACRES advises on the best transaction structure for your situation.', linkTo: '/contact', linkText: 'Discuss Options' },
    'how-long-does-it-take-to-sell-development-site': { title: 'Want a realistic timeline for your site?', description: 'Every property is different — let us assess yours.', linkTo: '/contact', linkText: 'Get a Timeline' },
    'when-is-best-time-to-sell-development-site': { title: 'Is now the right time to sell?', description: 'Get a current market assessment for your property.', linkTo: '/contact', linkText: 'Market Update' },
    'should-i-get-development-approval-before-selling': { title: 'DA or no DA? Let us advise', description: 'We help you decide the most profitable path to sale.', linkTo: '/contact', linkText: 'Get Advice' },
    'private-treaty-vs-eoi-vs-auction-development-sites': { title: 'Which sale method is right for you?', description: 'ACRES recommends the best approach for your specific site.', linkTo: '/contact', linkText: 'Discuss Strategy' },
    'how-infrastructure-projects-increase-land-value': { title: 'Is your property near new infrastructure?', description: 'Find out what it means for your land value.', linkTo: '/contact', linkText: 'Free Appraisal' },
    'why-your-corner-block-could-be-worth-more': { title: 'Own a corner block?', description: 'It could be worth significantly more than you think.', linkTo: '/contact', linkText: 'Get a Valuation' },
    'brisbane-suburbs-where-developers-are-actively-buying': { title: 'Is your suburb on the list?', description: 'Find out if developers are targeting your area.', linkTo: '/contact', linkText: 'Check Now' },
    'how-2032-olympics-will-affect-brisbane-property-values': { title: 'Capitalise on the Olympic effect', description: 'Understand how the Games impact your property value.', linkTo: '/contact', linkText: 'Talk to Us' },
    'understanding-brisbane-city-plan-2014-landowners-guide': { title: 'Need help reading your zoning?', description: 'We translate planning jargon into clear advice.', linkTo: '/contact', linkText: 'Free Consultation' },
    'what-is-site-consolidation-could-it-benefit-you': { title: 'Could your neighbours add value?', description: 'Site consolidation can dramatically increase your land price.', linkTo: '/contact', linkText: 'Explore Options' },
    'downsizing-selling-to-developer-fund-retirement': { title: 'Considering downsizing?', description: 'Selling to a developer could fund a comfortable retirement.', linkTo: '/contact', linkText: 'Free Assessment' },
  };
  const inlineCta = ctaMap[article.id] || { title: 'Speak with our team', description: 'ACRES provides expert property advisory across Australia.', linkTo: '/contact', linkText: 'Contact Us' };

  // Split content into two halves for the mid-article CTA
  const paragraphs = linkedContent.trim().split('\n\n');
  const midPoint = Math.floor(paragraphs.length / 2);
  const firstHalf = paragraphs.slice(0, midPoint).join('\n\n');
  const secondHalf = paragraphs.slice(midPoint).join('\n\n');

  // Extract pull quote — find the most impactful sentence from content
  const extractPullQuote = (content) => {
    const raw = (content || '').replace(/[#*`\[\]]/g, '');
    const sentences = raw.split(/(?<=[.!?])\s+/).filter(s => s.length > 60 && s.length < 200);
    // Prefer sentences with strong words
    const strong = sentences.find(s => /million|percent|significant|critical|important|key|most|premium|exclusive|development|potential|value|opportunity/i.test(s));
    return strong || sentences[Math.floor(sentences.length / 3)] || '';
  };
  const pullQuote = extractPullQuote(article.content);

  return (
    <div className="min-h-screen bg-white" data-testid="article-page">
      <SEO
        title={article.title}
        description={article.metaDescription}
        image={article.image}
        url={`/insights/${article.id}`}
        type="article"
        schemas={combinedSchema}
        datePublished={article.date}
        dateModified={article.dateModified || article.date}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-24 bg-[#042E0C]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16">
          <Breadcrumbs items={[{ name: 'Insights', url: '/insights' }, { name: article.category || 'Article', url: `/insights?category=${encodeURIComponent(article.category || '')}` }, { name: article.title }]} variant="dark" />
          <span className="inline-block bg-[#C4A36B] text-white text-xs font-semibold tracking-wider uppercase px-3 py-1 mb-6">
            {article.category}
          </span>
          <motion.h1
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#FFF4EA] mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="article-title"
          >
            {article.title}
          </motion.h1>
          <p className="text-[#FFF4EA]/70 text-lg mb-6 max-w-2xl">{article.excerpt}</p>
          <div className="flex items-center gap-6 text-sm text-[#FFF4EA]/50">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {new Date(article.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {article.readTime}</span>
            <Link
              to={article.author?.url || '/team/daniel-mccormack'}
              className="text-[#C4A36B] hover:text-[#FFF4EA] transition-colors"
              data-testid="article-author-link"
            >
              By {article.author?.name || 'Daniel McCormack'}
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <motion.div
          className="aspect-[21/9] overflow-hidden -mt-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </motion.div>
      </div>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="article-content">
        {/* Social Proof — seller articles only */}
        {isSellerArticle(article) && (
          <SocialProofBanner suburb={article.suburbs?.[0] || ''} />
        )}

        {/* Key Facts — structured for LLM extraction */}
        {article.keyFacts && article.keyFacts.length > 0 ? (
          <div className="bg-[#FFF4EA] border border-[#C4A36B]/20 p-6 sm:p-8 mb-10" data-speakable="key-facts" data-testid="key-facts">
            <h2 className="font-heading text-lg font-medium text-[#042E0C] mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#C4A36B] text-white flex items-center justify-center text-xs font-bold">i</span>
              Key Facts
            </h2>
            <ul className="space-y-2">
              {article.keyFacts.map((fact, i) => (
                <li key={sk(fact, i)} className="flex items-start gap-2 text-[#042E0C]/80 text-sm leading-relaxed">
                  <span className="text-[#C4A36B] mt-1 flex-shrink-0">&bull;</span>
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="bg-[#FFF4EA] border border-[#C4A36B]/20 p-6 sm:p-8 mb-10" data-speakable="summary" data-testid="article-summary">
            <h2 className="font-heading text-lg font-medium text-[#042E0C] mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#C4A36B] text-white flex items-center justify-center text-xs font-bold">i</span>
              Summary
            </h2>
            <p className="text-[#042E0C]/80 text-sm leading-relaxed">
              {article.metaDescription || article.excerpt}
            </p>
            <p className="text-[#042E0C]/50 text-xs mt-3">
              Source: ACRES — Australian Commercial & Residential Group | acres.au
            </p>
          </div>
        )}

        {isCms ? (
          /* CMS Article — render sanitized HTML via the audited <SafeHtml />
             wrapper. DOMPurify strips any <script>, on*= handlers, and
             javascript: URLs so authored content cannot inject XSS. */
          <SafeHtml
            html={mainContent}
            className="prose prose-lg max-w-none prose-headings:text-[#042E0C] prose-headings:font-light prose-h2:text-3xl prose-h2:border-b prose-h2:border-[#C4A36B]/30 prose-h2:pb-4 prose-h2:mt-16 prose-h3:text-xl prose-a:text-[#C4A36B] prose-blockquote:border-l-[#C4A36B] prose-table:border-collapse prose-td:border prose-td:border-[#042E0C]/15 prose-td:p-2 prose-th:border prose-th:border-[#042E0C]/15 prose-th:p-2 prose-th:bg-[#042E0C]/5 prose-img:rounded-lg prose-hr:border-[#C4A36B]/30"
          />
        ) : (
          <>
            {/* Table of Contents */}
            <TableOfContents content={mainContent} />

        {/* First Half */}
        <ReactMarkdown components={mdComponents} remarkPlugins={[remarkGfm]}>
          {firstHalf.trim()}
        </ReactMarkdown>

        {/* Pull Quote */}
        {pullQuote && (
          <figure className="my-14 relative" data-testid="pull-quote">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#C4A36B] to-[#C4A36B]/20" />
            <blockquote className="pl-8 pr-4">
              <p className="font-heading text-2xl sm:text-3xl font-light text-[#042E0C] leading-relaxed italic">
                "{pullQuote.trim()}"
              </p>
            </blockquote>
          </figure>
        )}

        {/* Mid-Article CTA */}
        <InlineCTA {...inlineCta} />

        {/* Lead Magnet — seller articles only */}
        {isSellerArticle(article) && (
          <LeadMagnetCTA guideSlug={getLeadMagnetSlug(article)} sourceArticle={article.id} />
        )}

        {/* Second Half */}
        <ReactMarkdown components={mdComponents} remarkPlugins={[remarkGfm]}>
          {secondHalf.trim()}
        </ReactMarkdown>
          </>
        )}

        {/* FAQ Section */}
        <FAQSection faq={article.faq} />

        {/* Suburbs Mentioned */}
        {mentionedSuburbs.length > 0 && (
          <div className="mt-12 bg-[#FFF4EA] border border-[#C4A36B]/20 p-6 sm:p-8" data-testid="suburbs-mentioned">
            <h3 className="font-heading text-lg font-light text-[#042E0C] mb-4">Suburbs Mentioned in This Article</h3>
            <div className="flex flex-wrap gap-2">
              {mentionedSuburbs.map(s => (
                <Link
                  key={s.slug}
                  to={`/suburb/${s.slug}`}
                  className="px-3 py-1.5 bg-white border border-[#042E0C]/10 text-sm text-[#042E0C] hover:border-[#C4A36B] hover:text-[#C4A36B] transition-colors"
                  data-testid={`suburb-tag-${s.slug}`}
                >
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Zoning Check Lead Form — shows on planning/zoning/development articles */}
        {(article.category === 'Guide' || article.id?.includes('zoning') || article.id?.includes('development-approval') || article.id?.includes('should-i-sell') || article.id?.includes('what-is-my') || article.id?.includes('property-worth') || article.id?.includes('can-i-build') || article.id?.includes('overlay') || article.id?.includes('planning') || article.id?.includes('what-can-i-build')) && (
          <ZoningCheckForm suburb={article.suburbs?.[0] || ''} sourceArticle={article.id} />
        )}

        {/* Development Enquiry Tool — seller articles only */}
        {isSellerArticle(article) && (
          <DevelopmentEnquiryTool source={article.id} suburb={article.suburbs?.[0] || ''} />
        )}

        {/* Citation Block — structured for LLM attribution */}
        <div className="mt-12 border-t border-[#042E0C]/10 pt-6" data-testid="citation-block">
          <p className="text-sm text-[#5B5B5B] tracking-wide">
            Published by <span className="text-[#042E0C] font-medium">ACRES</span> — Australian Commercial & Residential Group
          </p>
          <p className="text-xs text-[#5B5B5B]/60 mt-2">
            Source: acres.au/insights/{article.id} | ACRES (Australian Commercial & Residential Group) provides property advisory, development site sales, and residential real estate services across Brisbane and South East Queensland, Australia.
          </p>
        </div>

        {/* Author Byline — links to author profile so AI retrievers can resolve the Person entity. */}
        <div className="mt-8 bg-[#042E0C] p-6 sm:p-8 flex items-center gap-6" data-testid="author-byline">
          <Link to="/team/daniel-mccormack" className="w-16 h-16 rounded-full overflow-hidden bg-[#C4A36B]/20 flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/ibqouwi8_F9992471-15B8-4303-8FF6-01B35D3598D1.png"
              alt="Daniel McCormack"
              className="w-full h-full object-cover"
            />
          </Link>
          <div className="flex-1">
            <p className="font-heading text-[#FFF4EA] mb-1">Daniel McCormack</p>
            <p className="text-sm text-[#FFF4EA]/60 mb-2">Managing Director, ACRES — Australian Commercial &amp; Residential Group</p>
            <div className="flex flex-wrap gap-4">
              <a href="tel:0730960542" className="flex items-center gap-1.5 text-sm text-[#C4A36B] hover:text-[#FFF4EA] transition-colors">
                <Phone size={13} /> 07 3096 0542
              </a>
              <Link to="/team/daniel-mccormack" className="text-sm text-[#C4A36B] hover:text-[#FFF4EA] transition-colors">
                About the author →
              </Link>
              <Link to="/team" className="text-sm text-[#C4A36B] hover:text-[#FFF4EA] transition-colors">
                Meet the team →
              </Link>
            </div>
          </div>
        </div>

        {/* Canonical "About ACRES" entity reinforcement block — appears on every
            article so AI retrievers see the same brand definition consistently
            across the entire content corpus. This is the #1 lever for entity
            recognition in LLM ecosystems (per ChatGPT's AEO advisory). */}
        <aside
          className="mt-8 border border-[#042E0C]/10 bg-[#FFF4EA] p-6 sm:p-8"
          data-testid="entity-about-acres"
          aria-label="About ACRES"
        >
          <h2 className="font-heading text-xl font-light text-[#042E0C] mb-3">About ACRES</h2>
          <p className="text-sm text-[#042E0C]/80 leading-relaxed mb-3">
            <strong>ACRES — Australian Commercial &amp; Residential Group</strong> is a Brisbane-based property group specialising in
            {' '}<Link to="/development-sites" className="text-[#042E0C] underline decoration-[#C4A36B] underline-offset-2 hover:text-[#C4A36B]">development site sales</Link>,
            {' '}<Link to="/project-marketing" className="text-[#042E0C] underline decoration-[#C4A36B] underline-offset-2 hover:text-[#C4A36B]">project marketing</Link>,
            {' '}<Link to="/strategic-acquisitions" className="text-[#042E0C] underline decoration-[#C4A36B] underline-offset-2 hover:text-[#C4A36B]">strategic property acquisitions</Link>,
            residential real estate, capital structuring, and in-house property development.
          </p>
          <p className="text-sm text-[#042E0C]/80 leading-relaxed mb-3">
            Operating across Brisbane and South East Queensland, ACRES works with sellers, developers, investors, and project partners to identify value, shape strategy, and deliver stronger outcomes across residential, commercial, and development property markets.
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-xs">
            <Link to="/about" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">About ACRES →</Link>
            <Link to="/services" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">Our services →</Link>
            <Link to="/valuation" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">Free property valuation →</Link>
            <Link to="/insights" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">More insights →</Link>
            <Link to="/contact" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">Contact ACRES →</Link>
          </div>
        </aside>
      </article>

      {/* Bottom CTA */}
      <section className="bg-[#042E0C] py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-light text-[#FFF4EA] mb-4">
            Ready to discuss your property goals?
          </h2>
          <p className="text-[#FFF4EA]/60 mb-8 max-w-lg mx-auto">
            Whether you're buying, selling, or developing — our team provides tailored advice for every stage.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="px-8 py-3 bg-[#C4A36B] text-white font-medium text-sm tracking-wide hover:bg-[#FFF4EA] hover:text-[#042E0C] transition-colors" data-testid="cta-contact">
              Get in Touch
            </Link>
            <Link to="/residential" className="px-8 py-3 border border-[#C4A36B] text-[#C4A36B] font-medium text-sm tracking-wide hover:bg-[#C4A36B] hover:text-white transition-colors" data-testid="cta-browse">
              Browse Properties
            </Link>
            <Link to="/stock" className="px-8 py-3 border border-[#FFF4EA]/20 text-[#FFF4EA]/70 font-medium text-sm tracking-wide hover:border-[#C4A36B] hover:text-[#C4A36B] transition-colors" data-testid="cta-stock">
              View Development Sites
            </Link>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <RelatedArticles currentId={article.id} currentCategory={article.category} suburbRelated={suburbRelated} />

      <Footer />

      {/* Exit-Intent Popup — seller articles only */}
      {isSellerArticle(article) && (
        <ExitIntentPopup sourcePage={article.id} />
      )}
    </div>
  );
};

export default ArticlePage;
